import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabaseClient';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [contractors, setContractors] = useState([]);
  const [events, setEvents] = useState([]);
  const [schedules, setSchedules] = useState([]); // reserved for export logic
  const [importedForms, setImportedForms] = useState([]);
  const [activeForm, setActiveForm] = useState(null);
  const [assignments, setAssignments] = useState({}); // { eventId: [{ contractorId, position }] }

  const [isHydrating, setIsHydrating] = useState(true);

  // ---------- Helpers ----------

  const normalizeContractor = (data) => ({
    id: data.id || uuidv4(),
    name: (data.name || '').trim(),
    email: (data.email || '').trim(),
    phone: (data.phone || '').trim(),
    shirtSize: (data.shirtSize || '').trim(),
    primaryRole: (data.primaryRole || '').trim(),
    notes: data.notes || '',
  });

  const getFirst = (row, keys) => {
    for (const key of keys) {
      if (row[key] != null && String(row[key]).trim() !== '') {
        return String(row[key]).trim();
      }
    }
    return '';
  };

  const matchContractorFromRow = (row) => {
    const name = (row.name || '').trim();
    const email = (row.email || '').trim().toLowerCase();
    const phone = (row.phone || '').trim();

    // email + phone
    if (email && phone) {
      const byBoth = contractors.find(
        (c) =>
          c.email &&
          c.phone &&
          c.email.toLowerCase() === email &&
          c.phone === phone
      );
      if (byBoth) return byBoth;
    }

    // email only
    if (email) {
      const byEmail = contractors.find(
        (c) => c.email && c.email.toLowerCase() === email
      );
      if (byEmail) return byEmail;
    }

    // phone only
    if (phone) {
      const byPhone = contractors.find(
        (c) => c.phone && c.phone === phone
      );
      if (byPhone) return byPhone;
    }

    // name (last resort)
    if (name) {
      const byName = contractors.find(
        (c) =>
          (c.name || '').trim().toLowerCase() === name.toLowerCase()
      );
      if (byName) return byName;
    }

    return null;
  };

  // ---------- De-duplication & Contractor Management ----------

  const addContractor = useCallback((contractorData) => {
    const incomingBase = normalizeContractor(contractorData);

    if (!incomingBase.name && !incomingBase.email && !incomingBase.phone) {
      return;
    }

    setContractors((prev) => {
      const incoming = { ...incomingBase };

      // 1) exact key: email + phone
      const exactKeyIndex = prev.findIndex((c) => {
        const sameEmail =
          incoming.email &&
          c.email &&
          c.email.toLowerCase() === incoming.email.toLowerCase();
        const samePhone = incoming.phone && c.phone && c.phone === incoming.phone;
        return sameEmail && samePhone;
      });

      if (exactKeyIndex >= 0) {
        const existing = prev[exactKeyIndex];

        const sameName =
          (existing.name || '').trim().toLowerCase() ===
          incoming.name.toLowerCase();
        const sameShirt =
          (existing.shirtSize || '').trim().toLowerCase() ===
          incoming.shirtSize.toLowerCase();
        const sameRole =
          (existing.primaryRole || '').trim().toLowerCase() ===
          incoming.primaryRole.toLowerCase();

        if (sameName && sameShirt && sameRole) {
          return prev;
        }

        const overwrite = window.confirm(
          [
            `Contractor "${existing.name}" already exists with same email & phone.`,
            '',
            'Existing:',
            `• Name: ${existing.name || '—'}`,
            `• Phone: ${existing.phone || '—'}`,
            `• Email: ${existing.email || '—'}`,
            `• Shirt: ${existing.shirtSize || '—'}`,
            `• Role: ${existing.primaryRole || '—'}`,
            '',
            'New from CSV:',
            `• Name: ${incoming.name || '—'}`,
            `• Phone: ${incoming.phone || '—'}`,
            `• Email: ${incoming.email || '—'}`,
            `• Shirt: ${incoming.shirtSize || '—'}`,
            `• Role: ${incoming.primaryRole || '—'}`,
            '',
            'OK = overwrite with CSV details',
            'Cancel = keep existing contractor',
          ].join('\n')
        );

        if (!overwrite) {
          return prev;
        }

        const updated = [...prev];
        updated[exactKeyIndex] = {
          ...existing,
          ...incoming,
          id: existing.id,
        };
        return updated;
      }

      // 2) email OR phone match potential conflict
      const conflictIndex = prev.findIndex((c) => {
        const emailMatch =
          incoming.email &&
          c.email &&
          c.email.toLowerCase() === incoming.email.toLowerCase();
        const phoneMatch =
          incoming.phone && c.phone && c.phone === incoming.phone;
        return emailMatch || phoneMatch;
      });

      if (conflictIndex >= 0) {
        const existing = prev[conflictIndex];

        const overwrite = window.confirm(
          [
            `Contractor "${existing.name}" may be a duplicate (email or phone match).`,
            '',
            'Existing:',
            `• Name: ${existing.name || '—'}`,
            `• Phone: ${existing.phone || '—'}`,
            `• Email: ${existing.email || '—'}`,
            `• Shirt: ${existing.shirtSize || '—'}`,
            `• Role: ${existing.primaryRole || '—'}`,
            '',
            'New from CSV:',
            `• Name: ${incoming.name || '—'}`,
            `• Phone: ${incoming.phone || '—'}`,
            `• Email: ${incoming.email || '—'}`,
            `• Shirt: ${incoming.shirtSize || '—'}`,
            `• Role: ${incoming.primaryRole || '—'}`,
            '',
            'OK = overwrite with CSV details',
            'Cancel = keep existing contractor',
          ].join('\n')
        );

        if (!overwrite) {
          return prev;
        }

        const updated = [...prev];
        updated[conflictIndex] = {
          ...existing,
          ...incoming,
          id: existing.id,
        };
        return updated;
      }

      // 3) no conflict → add new
      return [
        ...prev,
        {
          ...incoming,
          id: uuidv4(),
        },
      ];
    });
  }, []);

  const deleteContractor = useCallback((contractorId) => {
    setContractors((prev) => prev.filter((c) => c.id !== contractorId));

    setAssignments((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((eventId) => {
        const filtered = (updated[eventId] || []).filter(
          (a) => a.contractorId !== contractorId
        );
        if (filtered.length > 0) {
          updated[eventId] = filtered;
        } else {
          delete updated[eventId];
        }
      });
      return updated;
    });
  }, []);

  const clearContractors = useCallback(() => {
    if (
      !window.confirm(
        'Delete ALL contractors and clear their assignments? This cannot be undone.'
      )
    ) {
      return;
    }
    setContractors([]);
    setAssignments({});
  }, []);

  // ---------- CSV Import (tuned to your NASCAR form) ----------

  const importCSVData = useCallback(
    (fileName, csvData) => {
      const form = {
        id: uuidv4(),
        name: fileName,
        uploadDate: new Date().toISOString(),
        headers: csvData.headers,
        data: csvData.data,
        eventColumns: [],
        contractorRows: [],
      };

      const datePattern =
        /\d{1,2}[\s,\/\-]+\d{1,2}[\s,\/\-]+\d{2,4}|\w+\s+\d{1,2}[\s,\/\-]+\d{2,4}/i;

      const eventColumns = csvData.headers
        .map((header, index) => {
          if (!header) return null;

          const rawHeader = header;
          const headerStr = String(header);
          const trimmed = headerStr.trim();

          if (datePattern.test(trimmed)) {
            const lines = trimmed
              .split('\n')
              .map((l) => l.trim())
              .filter(Boolean);

            const dateStr = lines[0] || trimmed;
            const eventName =
              lines.length > 1 ? lines[lines.length - 1] : `Event ${index + 1}`;

            return {
              index,
              csvKey: rawHeader,
              header: headerStr,
              dateStr,
              eventName,
              id: uuidv4(),
            };
          }

          return null;
        })
        .filter(Boolean);

      form.eventColumns = eventColumns;

      const NAME_KEYS = ['First and Last Name:', 'Name', 'Contractor'];
      const EMAIL_KEYS = ['Email Address', 'Email', 'Email address'];
      const PHONE_KEYS = ['Phone Number:', 'Phone Number', 'Phone'];
      const SHIRT_KEYS = ['Shirt Size:', 'Shirt Size', 'Shirt'];
      const ROLE_KEYS = ['Primary Role', 'Role'];

      const contractorRows = csvData.data.map((row) => {
        const contractor = {
          name: getFirst(row, NAME_KEYS),
          email: getFirst(row, EMAIL_KEYS),
          phone: getFirst(row, PHONE_KEYS),
          shirtSize: getFirst(row, SHIRT_KEYS),
          primaryRole: getFirst(row, ROLE_KEYS),
          availability: {},
        };

        eventColumns.forEach((event) => {
          const raw = row[event.csvKey];
          const normalized = (raw == null ? '' : String(raw))
            .trim()
            .toLowerCase();

          const isAvailable =
            normalized.startsWith('available') ||
            normalized === 'yes' ||
            normalized === 'y' ||
            normalized === 'true' ||
            normalized === '1';

          contractor.availability[event.id] = isAvailable;
        });

        return contractor;
      });

      form.contractorRows = contractorRows;

      contractorRows.forEach((row) => {
        if (row.name || row.email || row.phone) {
          addContractor({
            name: row.name,
            email: row.email,
            phone: row.phone,
            shirtSize: row.shirtSize,
            primaryRole: row.primaryRole,
          });
        }
      });

      const newEvents = eventColumns.map((col) => ({
        id: col.id,
        name: col.eventName,
        date: col.dateStr,
        formId: form.id,
        formName: fileName,
      }));

      setEvents((prev) => [...prev, ...newEvents]);
      setImportedForms((prev) => [...prev, form]);

      return form;
    },
    [addContractor]
  );

  const deleteForm = useCallback((formId) => {
    setImportedForms((prev) => prev.filter((f) => f.id !== formId));

    setEvents((prevEvents) => {
      const remaining = prevEvents.filter((e) => e.formId !== formId);
      const removedEventIds = prevEvents
        .filter((e) => e.formId === formId)
        .map((e) => e.id);

      if (removedEventIds.length > 0) {
        setAssignments((prevAssignments) => {
          const updated = { ...prevAssignments };
          removedEventIds.forEach((id) => {
            delete updated[id];
          });
          return updated;
        });
      }

      return remaining;
    });

    setActiveForm((prev) => (prev === formId ? null : prev));
  }, []);

  // ---------- Assignments & Queries ----------

  const assignContractor = useCallback(
    (eventId, contractorId, position = 'Crew') => {
      setAssignments((prev) => {
        const eventAssignments = prev[eventId] || [];
        const existingIndex = eventAssignments.findIndex(
          (a) => a.contractorId === contractorId && a.position === position
        );

        if (existingIndex >= 0) {
          const updated = [...eventAssignments];
          updated[existingIndex] = { contractorId, position };
          return { ...prev, [eventId]: updated };
        }

        return {
          ...prev,
          [eventId]: [...eventAssignments, { contractorId, position }],
        };
      });
    },
    []
  );

  const unassignContractor = useCallback((eventId, contractorId) => {
    setAssignments((prev) => {
      const eventAssignments = prev[eventId] || [];
      const filtered = eventAssignments.filter(
        (a) => a.contractorId !== contractorId
      );
      return { ...prev, [eventId]: filtered };
    });
  }, []);

  const getAvailableContractors = useCallback(
    (eventId) => {
      if (!activeForm) return [];

      const event = events.find((e) => e.id === eventId);
      if (!event) return [];

      const form = importedForms.find((f) => f.id === event.formId);
      if (!form) return [];

      const seen = new Set();
      const result = [];

      form.contractorRows
        .filter((row) => row.availability[eventId])
        .forEach((row) => {
          const contractor = matchContractorFromRow(row);
          if (contractor && !seen.has(contractor.id)) {
            seen.add(contractor.id);
            result.push(contractor);
          }
        });

      return result;
    },
    [activeForm, events, importedForms, contractors]
  );

  const getEventAssignments = useCallback(
    (eventId) => {
      const eventAssignments = assignments[eventId] || [];
      return eventAssignments.map((assignment) => {
        const contractor = contractors.find(
          (c) => c.id === assignment.contractorId
        );
        return {
          ...assignment,
          contractor,
        };
      });
    },
    [assignments, contractors]
  );

  const clearAllData = useCallback(() => {
    if (
      !window.confirm(
        'Reset ALL forms, contractors, events, and assignments? This cannot be undone.'
      )
    ) {
      return;
    }
    setContractors([]);
    setEvents([]);
    setSchedules([]);
    setImportedForms([]);
    setActiveForm(null);
    setAssignments({});
  }, []);

  // ---------- Supabase: Hydrate on load ----------

  useEffect(() => {
    const loadState = async () => {
      try {
        const { data, error } = await supabase
          .from('app_state')
          .select('state_json')
          .eq('id', 'global')
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading app_state:', error.message);
        }

        if (data && data.state_json) {
          const {
            contractors: c = [],
            events: e = [],
            importedForms: f = [],
            activeForm: af = null,
            assignments: a = {},
          } = data.state_json;

          setContractors(Array.isArray(c) ? c : []);
          setEvents(Array.isArray(e) ? e : []);
          setImportedForms(Array.isArray(f) ? f : []);
          setActiveForm(af);
          setAssignments(a || {});
        }
      } catch (err) {
        console.error('Unexpected error loading app_state:', err);
      } finally {
        setIsHydrating(false);
      }
    };

    loadState();
  }, []);

  // ---------- Supabase: Save on change (debounced) ----------

  useEffect(() => {
    if (isHydrating) return;

    const payload = {
      contractors,
      events,
      importedForms,
      activeForm,
      assignments,
    };

    const timeout = setTimeout(async () => {
      try {
        const { error } = await supabase
          .from('app_state')
          .upsert({
            id: 'global',
            state_json: payload,
          });

        if (error) {
          console.error('Error saving app_state:', error.message);
        }
      } catch (err) {
        console.error('Unexpected error saving app_state:', err);
      }
    }, 500); // debounce saves

    return () => clearTimeout(timeout);
  }, [isHydrating, contractors, events, importedForms, activeForm, assignments]);

  const value = {
    contractors,
    events,
    schedules,
    importedForms,
    activeForm,
    assignments,
    setActiveForm,
    addContractor,
    deleteContractor,
    clearContractors,
    importCSVData,
    deleteForm,
    assignContractor,
    unassignContractor,
    getAvailableContractors,
    getEventAssignments,
    clearAllData,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
